import APIs, { PropertyApi, ServicesApi } from "../../apis";
import { Constants, Enums, Tools } from "../../utils";
import Localization from "../../localization";
import { useApi, useAuth } from "../../hooks";
import { useParams } from "react-router";
import { SVGs } from "../../assets";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const smhiSupportedDevices = {
  [Enums.deviceTypes.ajax]: {
    title: Localization.general.ajax_smhi,
    logo: SVGs.ajaxSmhi,
    helpUrl: Constants.Project.smhiAjaxHelpUrl,
  },
};

const ServiceContext = createContext({});

const ViewModel = ({ children }) => {
  const [data, setData] = useState({ device: {}, paymentInfo: {} });
  const [loading, setLoading] = useState(false);
  const editAlarmConnectionModalRef = useRef();
  const editAlarmOperatorModalRef = useRef();
  const { user, refreshUser } = useAuth();
  const editLocationModalRef = useRef();
  const editAlarmSMSModalRef = useRef();
  const [error, setError] = useState();
  const editGroupsModalRef = useRef();
  const editSMHIModalRef = useRef();
  const { uuid } = useParams();
  const { execute } = useApi();

  const smhiSupportedDevice = smhiSupportedDevices[data?.device?.type];
  const progress = Object.keys(data?.configuration?.progress ?? {}).length;
  const isConfigured = useMemo(
    () => data.state !== Enums.subscriptionState.unConfigured,
    [data.state]
  );
  const home = useMemo(
    () => ({
      address: data?.device?.name ?? "",
      coords: data?.device?.location ?? {},
      note: data?.device?.distribution?.message ?? "",
    }),
    [data?.device]
  );
  const alarmModel = useMemo(
    () => ({
      ...(data?.device?.alarm ?? {}),
      identifier: data?.device?.identifier,
    }),
    [data?.device]
  );
  const collaborators = {
    groups: data?.device?.distribution?.recipients?.groups ?? [],
    total: data?.device?.distribution?.recipients?.nrOfRecipients ?? 0,
  };
  const arc = {
    confirmed: data?.device?.arcConfirmed,
  };

  const cardExtraData = useMemo(
    () => ({
      [Enums.serviceCardIDs.location]: {
        stepTitle: Localization.service.step_location_title,
        onBtnClick: (a, b) => editLocationModalRef.current.open(a, b),
      },
      [Enums.serviceCardIDs.alarmConnection]: {
        readMoreUrl: Constants.Project.urls.connect,
        stepTitle: Localization.service.step_alarm_connection_title,
        onBtnClick: (a, b) => editAlarmConnectionModalRef.current.open(a, b),
      },
      [Enums.serviceCardIDs.connect]: {
        readMoreUrl: Constants.Project.urls.connect,
        stepTitle: Localization.service.step_connect_title,
        onBtnClick: (a, b) => editGroupsModalRef.current.open(a, b),
      },
      [Enums.serviceCardIDs.alarmCentral]: {
        readMoreUrl: Constants.Project.urls.alarmCentral,
        stepTitle: Localization.service.step_alarm_central_title,
        onBtnClick: (a, b) => editAlarmOperatorModalRef.current.open(a, b),
      },
      [Enums.serviceCardIDs.alarmSms]: {
        readMoreUrl: Constants.Project.alarmSMSHelpUrl,
        stepTitle: Localization.service.step_alarm_sms_title,
        onBtnClick: (...props) => editAlarmSMSModalRef.current.open(...props),
      },
      [Enums.serviceCardIDs.smhi]: {
        stepTitle: Localization.service.step_smhi_title,
        onBtnClick: (...props) => editSMHIModalRef.current.open(...props),
      },
    }),
    []
  );

  const cards = useMemo(
    () =>
      (data?.configuration?.cards ?? []).map((_, index) => {
        const nextCard = data.configuration?.cards[index + 1];
        return {
          ..._,
          ...cardExtraData[_.id],
          activated: (data?.configuration?.progress ?? {})[_.id],
          progress:
            nextCard?.id === Enums.serviceCardIDs.alarmConnection &&
            !Object.values(Enums.deviceIntegrations).includes(
              data?.device?.alarm?.integration
            )
              ? [_.id, Enums.serviceCardIDs.alarmConnection]
              : nextCard?.id === Enums.serviceCardIDs.smhi &&
                !!smhiSupportedDevice
              ? [_.id, Enums.serviceCardIDs.smhi]
              : _.id,
        };
      }),
    [
      cardExtraData,
      smhiSupportedDevice,
      data.configuration?.cards,
      data.configuration?.progress,
      data?.device?.alarm?.integration,
    ]
  );

  useEffect(() => {
    getService();
  }, []);

  const getSubscription = async () => {
    const subscription = await APIs.subscriptions.subscription(uuid);
    subscription.device = {
      ...data.device,
      ...subscription.configuration.device,
    };
    subscription.device.distribution = subscription.device.distributions[0];
    return subscription;
  };

  const updateSubscription = async () => {
    const res = await getSubscription();
    setData((data) => ({ ...data, ...res }));
  };

  const getService = useCallback(
    async (shouldLoad = true, { progress = "" } = {}) => {
      setLoading(shouldLoad);
      try {
        if (!!progress) await updateProgress(progress, false);
        const subscription = await getSubscription();

        subscription.device.alarm = await APIs.devices.type(
          subscription.device.type
        );

        const event = await APIs.events.event(subscription.device.id);
        subscription.device.distribution.webhooks = event[0]?.webhooks ?? [];

        subscription.device.alarmSMS = await APIs.devices.alarmRecipients(
          subscription.device.id
        );

        const cardIndex = cards.findIndex((_) => _.id === progress);
        if (!!progress && cardIndex !== -1) {
          if (cardIndex !== cards.length - 1 && !isConfigured) {
            document.getElementById(cards[cardIndex + 1].id).scrollIntoView({
              behavior: "smooth",
            });
          }
        }
        setData(subscription);
      } catch (error) {
        if (!!error)
          setError({
            status: error.response.status,
            title: error.response.status,
          });
      } finally {
        setLoading(false);
      }
    },
    [uuid, data, !isConfigured]
  );

  const createHome = async ({ title, address, coords, note }) => {
    await execute(async () => {
      const { id } = await PropertyApi.createProperty(uuid, {
        title,
        address,
        latitude: coords.latitude,
        longitude: coords.longitude,
      });
      await PropertyApi.addPropertyToConnect(uuid, id);
      if (!!note)
        await APIs.events.update(data?.device.id, {
          ...data?.device.distribution,
          message: note,
        });
    });
    await refreshUser();
    await getService(false, {
      progress: editLocationModalRef?.current?.data?.progress,
    });
    editLocationModalRef.current.close("");
  };

  const addHome = async ({ id, note = "" }) => {
    await execute(async () => {
      if (data?.device?.distribution?.message !== note)
        await APIs.events.update(data?.device.id, {
          ...data?.device.distribution,
          message: note,
        });
      await PropertyApi.addPropertyToConnect(uuid, id);
      await refreshUser();
      await getService(false, {
        progress: editLocationModalRef?.current?.data?.progress,
      });
      editLocationModalRef.current.close("");
    });
  };

  const addAlarmModel = async (alarm) => {
    try {
      await execute(() => ServicesApi.updateDevice(data?.device.id, alarm));
      await getService(false, {
        progress: editAlarmConnectionModalRef?.current?.data?.progress,
      });
    } catch (error) {}
  };

  const getArc = async () => {
    try {
      const arc = await execute(() => ServicesApi.arc(uuid));
      if (!arc.actions?.length)
        arc.actions = Object.keys(Enums.arcActionTypes).map((type) => ({
          type,
          action: Enums.details(type).defaultAction,
        }));
      return arc;
    } catch (error) {}
  };

  const updateArc = async (arc) => {
    await execute(() => ServicesApi.updateArc(uuid, arc));
    await getService(false, {
      progress: editAlarmOperatorModalRef?.current?.data?.progress,
    });
    editAlarmOperatorModalRef.current.close("");
  };

  const loadWebhook = async () => {
    const event = await APIs.events.event(data.device.id);
    setData((data) => {
      data.device.distribution.webhooks = event[0]?.webhooks ?? [];
      return { ...data };
    });
  };

  const updateWebhooks = async (webhooks = []) => {
    await APIs.events.update(data.device.id, {
      ...data.device.distribution,
      webhooks,
    });
    await loadWebhook();
    if (webhooks.length === 1 && webhooks[0].enabled === undefined) {
      await updateProgress(Enums.serviceCardIDs.smhi);
    }
  };

  const updateWebhookSignal = async (payload = {}) => {
    await APIs.events.updateWebhookSignal(data.device.id, payload);
    await loadWebhook();
  };

  const testWebhookSignal = async (webhook) => {
    return APIs.events.testWebhook(data.device.id, webhook);
  };

  const loadDeviceAlarmRecipient = async (
    params = {
      offset: 0,
      limit: data.device.alarmSMS?.recipients?.length ?? 0,
    }
  ) => {
    const alarmSMS = await APIs.devices.alarmRecipients(data.device.id, params);
    setData((data) => {
      const recipients = data.device.alarmSMS.recipients;
      data.device.alarmSMS = alarmSMS;
      data.device.alarmSMS.recipients = !params?.offset
        ? alarmSMS.recipients
        : [...recipients, ...alarmSMS.recipients];
      return { ...data };
    });
  };

  const modifyDeviceAlarmRecipient = async (payload) => {
    const isNew = !payload.id;
    if (!!payload.owner) payload.name = user.alias;
    await (isNew
      ? APIs.devices.addAlarmRecipient(data.device.id, payload)
      : APIs.devices.updateAlarmRecipient(data.device.id, payload));
    await loadDeviceAlarmRecipient(
      !isNew
        ? undefined
        : {
            offset: 0,
            limit: 5 * Math.ceil(data.device.alarmSMS.recipients.length / 5),
          }
    );
  };

  const removeDeviceAlarmRecipient = async (id) => {
    await APIs.devices.removeAlarmRecipient(data.device.id, { id });
    await loadDeviceAlarmRecipient();
  };

  const modifyDeviceAlarmRecipientCategories = async (categories = []) => {
    await APIs.devices.updateAlarmRecipientCategories(
      data.device.id,
      categories
    );
    await loadDeviceAlarmRecipient();
  };

  const updateProgress = async (progress, sync = true) => {
    let params = { ...(data?.configuration?.progress ?? {}) };
    if (Tools.isObject(progress)) {
      params = { ...params, ...progress };
    } else {
      (Array.isArray(progress) ? progress : [progress]).forEach((progress) => {
        params[progress] = true;
      });
    }
    await ServicesApi.updateProgress(uuid, params);
    if (sync)
      setData((data) => {
        data.configuration.progress = params;
        return { ...data };
      });
    if (
      !isConfigured &&
      Object.keys(params).includes(cards[cards.length - 1]?.id)
    )
      await ServicesApi.configured(uuid);
  };

  const deleteSubscription = async (params) => {
    await ServicesApi.delSubscription(uuid, params);
    await getService(false);
  };

  const reactivateSubscription = async ({ activated }) => {
    if (activated) {
      await ServicesApi.reactivateSubscription(uuid);
      await getService(false);
    } else {
      window.open(
        `${Constants.Project.baseUrl}/rest/payments/restart/checkout/${data.licenseKey}`,
        "_self"
      );
    }
  };

  const updateDistribution = async ({ progress, ...options } = {}) => {
    await execute(() =>
      APIs.events.update(data.device.id, {
        ...data.device.distribution,
        ...options,
      })
    );
    await getService(false, { progress });
  };

  const isDeviceTypeEditable = (alarm) =>
    [Enums.deviceIntegrations.ajax, Enums.deviceIntegrations.sms].includes(
      alarm?.integration
    );

  const getClosestCircleMembers = async () => {
    const members = await APIs.groups.members(data.group?.id);
    const invites = await APIs.groups.invites(data.group?.id);

    setData((data) => {
      data.group.nrOfMembers = members.length;
      return { ...data };
    });

    return [
      ...members
        .map((_) => _.user)
        .reduce(
          (members, member) =>
            member.userId === user.userId
              ? [{ ...member, owner: true }, ...members]
              : [...members, member],
          []
        ),
      ...invites.map((_) => ({ ..._, invited: true })),
    ];
  };

  return (
    <ServiceContext.Provider
      children={children}
      value={{
        arc,
        home,
        data,
        uuid,
        error,
        cards,
        loading,
        progress,
        alarmModel,
        isConfigured,
        collaborators,
        name: data.name,
        group: data.group,
        device: data.device,
        smhiSupportedDevice,
        upgrade: data.upgrade,
        homes: user?.property ?? [],
        paymentInfo: data.paymentInfo,
        webhooks: data?.device?.distribution?.webhooks ?? [],
        alarmSMS: {
          ...data.device.alarmSMS,
          configured: data?.configuration?.progress?.alarm_sms !== undefined,
          activated: !!data?.configuration?.progress?.alarm_sms,
        },
        refs: {
          editSMHIModal: editSMHIModalRef,
          editGroupsModal: editGroupsModalRef,
          editLocationModal: editLocationModalRef,
          editAlarmSMSModal: editAlarmSMSModalRef,
          editAlarmOperatorModal: editAlarmOperatorModalRef,
          editAlarmConnectionModal: editAlarmConnectionModalRef,
        },
        getArc,
        addHome,
        updateArc,
        createHome,
        getService,
        addAlarmModel,
        updateWebhooks,
        updateProgress,
        testWebhookSignal,
        updateSubscription,
        updateDistribution,
        deleteSubscription,
        refresh: getService,
        updateWebhookSignal,
        isDeviceTypeEditable,
        reactivateSubscription,
        getClosestCircleMembers,
        loadDeviceAlarmRecipient,
        modifyDeviceAlarmRecipient,
        removeDeviceAlarmRecipient,
        modifyDeviceAlarmRecipientCategories,
        getCard: (id) => cards.find((_) => _.id === id),
      }}
    />
  );
};

const useViewModel = () => {
  const viewModel = useContext(ServiceContext);
  return viewModel;
};

export { ViewModel };
export default useViewModel;
